import { useState } from "react";
import { ErrorCodes, ProblemError } from "@csa/web-foundation";
import { useT } from "../../i18n/index";
import {
  CODE_PATTERN,
  SALES_STATUSES,
  type CreateProductInput,
  type ProductVersionInput,
  type SalesStatus,
} from "../../api/catalog-contracts";
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  Input,
  LoadingState,
  Select,
  StatusLabel,
  Table,
  TableCard,
  type StatusTone,
} from "../../ui";
import { useCarriers } from "./use-catalog";
import { useCatalogLines } from "./use-catalog-mgmt";
import {
  useAddProductVersion,
  useCreateProduct,
  useProduct,
  useProducts,
} from "./use-product-catalog";

// Products management (catalog step 2, ADR 0004 §4). Products carry the same governed,
// append-only versioning contract as carriers: an immutable identity (code + carrier +
// line) and whole-state versions.
//
// A9 — GET /v1/catalog/products now exists, so this panel PICKS from the real list
// instead of asking the operator to type a code from memory. The free-text lookup is
// kept as a secondary path (an operator who knows the code shouldn't have to scan a
// dropdown), but it is no longer the only way in.
// RBAC (catalog:write @ all) is UX; the API enforces regardless.

const SALES_TONE: Record<SalesStatus, StatusTone> = {
  ACTIVE: "active",
  PAUSED: "expiring",
  DISCONTINUED: "ended",
};

export function ProductManagement({ canWrite }: { canWrite: boolean }) {
  const { t } = useT();
  const [lookup, setLookup] = useState<string | null>(null);

  return (
    <section className="surf-stack" data-testid="prod-panel">
      <div className="surf-head">
        <div>
          <h1 className="surf-title">{t("prod.heading")}</h1>
          <p className="surf-sub muted">{t("prod.governedNote")}</p>
        </div>
      </div>

      <ProductPicker selected={lookup} onPick={setLookup} />
      <ProductLookup onLookup={setLookup} />
      {lookup && <ProductDetail code={lookup} canWrite={canWrite} />}
      <AddProductForm canWrite={canWrite} onCreated={setLookup} />
    </section>
  );
}

// A9 — the real product list. Honest-empty: a catalog with no products yet renders the
// empty state, not a dropdown with nothing in it.
function ProductPicker({
  selected,
  onPick,
}: {
  selected: string | null;
  onPick: (code: string) => void;
}) {
  const { t } = useT();
  const q = useProducts();

  if (q.isLoading) return <LoadingState label={t("prod.list.loading")} />;
  const rows = q.data?.data ?? [];
  // A FAILED READ IS NOT AN EMPTY CATALOG. "No products yet" invites the operator
  // to create duplicates of products that already exist.
  if (q.error != null)
    return (
      <Card className="cat-formcard">
        <ErrorState title={t("prod.list.loadFailed")} />
      </Card>
    );
  if (rows.length === 0)
    return (
      <Card className="cat-formcard">
        <EmptyState icon="records" title={t("prod.list.empty")} />
      </Card>
    );

  return (
    <TableCard title={t("prod.list.title")}>
      <Table>
        <thead>
          <tr>
            <th>{t("cat.list.code")}</th>
            <th>{t("prod.name")}</th>
            <th>{t("prod.salesStatus")}</th>
            <th className="cat-td-right" />
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => (
            <tr key={p.id} data-testid="prod-row">
              <td>
                <code>{p.code}</code>
              </td>
              <td className="cat-strong">{p.current.name}</td>
              <td>
                <StatusLabel tone={SALES_TONE[p.current.salesStatus]}>
                  {t(`prod.sales.${p.current.salesStatus}`)}
                </StatusLabel>
              </td>
              <td className="cat-td-right">
                <Button
                  variant="ghost"
                  size="sm"
                  type="button"
                  data-testid={`prod-pick-${p.code}`}
                  onClick={() => onPick(p.code)}
                >
                  {selected === p.code ? t("prod.list.picked") : t("cat.detail")}
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </TableCard>
  );
}

// Secondary path: an operator who already knows the immutable code jumps straight to it.
function ProductLookup({ onLookup }: { onLookup: (code: string) => void }) {
  const { t } = useT();
  const [draft, setDraft] = useState("");
  return (
    <Card className="cat-formcard">
      <div className="surf-cardhead">
        <span className="surf-cardtitle">{t("prod.lookup.title")}</span>
      </div>
      <p className="muted surf-note">{t("prod.lookup.note")}</p>
      <form
        data-testid="prod-lookup-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (draft.trim()) onLookup(draft.trim());
        }}
      >
        <fieldset className="k-form-grid">
          <Input
            label={t("cat.list.code")}
            placeholder="allianz-tamamlayici"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
        </fieldset>
        <div className="k-form-actions">
          <Button variant="secondary" type="submit" disabled={draft.trim() === ""}>
            {t("prod.lookup.submit")}
          </Button>
        </div>
      </form>
    </Card>
  );
}

function ProductDetail({ code, canWrite }: { code: string; canWrite: boolean }) {
  const { t } = useT();
  const product = useProduct(code);

  return (
    <div className="cat-detail surf-stack" data-testid="prod-detail">
      <div className="cat-detail-head">
        <span className="cat-detail-eyebrow muted">{t("prod.detailEyebrow")}</span>
        <h2 className="cat-detail-name">
          {product.data ? product.data.current.name : code} <code>{code}</code>
        </h2>
      </div>

      {product.isLoading && <LoadingState label={t("prod.loading")} rows={2} />}
      {product.error instanceof ProblemError && (
        <p role="alert" className="form-error">
          {product.error.problem.detail}
        </p>
      )}
      {product.data === null && (
        <EmptyState data-testid="prod-notfound" icon="records" title={t("prod.notFound")} />
      )}
      {product.data && (
        <Card>
          <div className="cat-kv">
            <div>
              <span className="muted surf-note">{t("prod.current")}</span>
              <p className="cat-strong">
                v{product.data.current.version}
                {product.data.current.effectiveTo === null && ` · ${t("cat.versions.current")}`}
              </p>
            </div>
            <div>
              <span className="muted surf-note">{t("prod.salesStatus")}</span>
              <p>
                <StatusLabel tone={SALES_TONE[product.data.current.salesStatus]}>
                  {t(`prod.sales.${product.data.current.salesStatus}`)}
                </StatusLabel>
              </p>
            </div>
            <div>
              <span className="muted surf-note">{t("prod.targetSegment")}</span>
              <p className="cat-strong">{product.data.current.targetSegment ?? "—"}</p>
            </div>
          </div>
        </Card>
      )}

      <AddProductVersionForm code={code} canWrite={canWrite} />
    </div>
  );
}

// Append a product version — the FULL next-state (the server closes the open version
// and opens this one). A version is not an identity write (no code/carrier/line).
function AddProductVersionForm({ code, canWrite }: { code: string; canWrite: boolean }) {
  const { t } = useT();
  const add = useAddProductVersion(code);
  const [name, setName] = useState("");
  const [targetSegment, setTargetSegment] = useState("");
  const [salesStatus, setSalesStatus] = useState<SalesStatus>("ACTIVE");

  const canSubmit = canWrite && name.trim() !== "" && !add.isPending;

  function submit() {
    const body: ProductVersionInput = { name: name.trim(), salesStatus };
    if (targetSegment.trim()) body.targetSegment = targetSegment.trim();
    add.mutate(body);
  }

  return (
    <Card className="cat-formcard">
      <div className="surf-cardhead">
        <span className="surf-cardtitle">{t("prod.version.add")}</span>
        <span className="cat-append-pill">{t("cat.appendOnlyTag")}</span>
      </div>
      {!canWrite && <p className="muted surf-note">{t("cat.readOnly")}</p>}
      <p className="muted surf-note">{t("cat.versions.appendOnlyNote")}</p>

      <form
        data-testid="prod-version-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) submit();
        }}
      >
        <fieldset className="k-form-grid" disabled={!canWrite}>
          <Input label={t("prod.name")} value={name} onChange={(e) => setName(e.target.value)} />
          <Input
            label={t("prod.targetSegment")}
            value={targetSegment}
            onChange={(e) => setTargetSegment(e.target.value)}
          />
          <SalesStatusSelect value={salesStatus} onChange={setSalesStatus} />
        </fieldset>
        <div className="k-form-actions">
          <Button variant="primary" type="submit" disabled={!canSubmit} loading={add.isPending}>
            {t("prod.version.submit")}
          </Button>
          {add.isSuccess && (
            <span role="status" className="form-ok">
              {t("prod.version.success")}
            </span>
          )}
          {add.error instanceof ProblemError && (
            <span role="alert" className="form-error">
              {add.error.problem.detail}
            </span>
          )}
        </div>
      </form>
    </Card>
  );
}

// Create a product — identity + version 1. The code is the immutable identity; carrier
// and line come from the catalog pickers; a duplicate code returns 409.
function AddProductForm({
  canWrite,
  onCreated,
}: {
  canWrite: boolean;
  onCreated: (code: string) => void;
}) {
  const { t } = useT();
  const create = useCreateProduct();
  const carriers = useCarriers();
  const lines = useCatalogLines();
  const [code, setCode] = useState("");
  const [carrierCode, setCarrierCode] = useState("");
  const [lineCode, setLineCode] = useState("");
  const [name, setName] = useState("");
  const [targetSegment, setTargetSegment] = useState("");
  const [salesStatus, setSalesStatus] = useState<SalesStatus>("ACTIVE");

  const codeInvalid = code.trim() !== "" && !CODE_PATTERN.test(code.trim());
  const canSubmit =
    canWrite &&
    code.trim() !== "" &&
    !codeInvalid &&
    carrierCode !== "" &&
    lineCode !== "" &&
    name.trim() !== "" &&
    !create.isPending;

  function submit() {
    const body: CreateProductInput = {
      code: code.trim(),
      carrierCode,
      lineCode,
      name: name.trim(),
      salesStatus,
    };
    if (targetSegment.trim()) body.targetSegment = targetSegment.trim();
    create.mutate(body, { onSuccess: (res) => onCreated(res.code) });
  }

  const isConflict = create.error instanceof ProblemError && create.error.is(ErrorCodes.CONFLICT);

  return (
    <Card className="cat-formcard">
      <div className="surf-cardhead">
        <span className="surf-cardtitle">{t("prod.add")}</span>
      </div>
      {!canWrite && <p className="muted surf-note">{t("cat.readOnly")}</p>}
      <p className="muted surf-note">{t("prod.governedNote")}</p>

      <form
        data-testid="prod-create-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) submit();
        }}
      >
        <fieldset className="k-form-grid" disabled={!canWrite}>
          <Input
            label={t("cat.list.code")}
            placeholder="allianz-tamamlayici"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            hint={t("prod.codeHint")}
            error={codeInvalid ? t("cat.carrier.invalidCode") : undefined}
            errorTestId="prod-err-code"
          />
          <Select
            label={t("prod.carrier")}
            value={carrierCode}
            onChange={(e) => setCarrierCode(e.target.value)}
          >
            <option value="">—</option>
            {(carriers.data?.data ?? []).map((c) => (
              <option key={c.code} value={c.code}>
                {c.current.tradeName}
              </option>
            ))}
          </Select>
          <Select
            label={t("cat.auth.line")}
            value={lineCode}
            onChange={(e) => setLineCode(e.target.value)}
          >
            <option value="">—</option>
            {(lines.data?.data ?? []).map((l) => (
              <option key={l.code} value={l.code}>
                {l.name}
              </option>
            ))}
          </Select>
          {lines.error != null && (
            <p className="form-error" role="alert" data-testid="lines-load-failed">
              {t("cat.linesLoadFailed")}
            </p>
          )}
          <Input label={t("prod.name")} value={name} onChange={(e) => setName(e.target.value)} />
          <Input
            label={t("prod.targetSegment")}
            value={targetSegment}
            onChange={(e) => setTargetSegment(e.target.value)}
          />
          <SalesStatusSelect value={salesStatus} onChange={setSalesStatus} />
        </fieldset>
        <div className="k-form-actions">
          <Button variant="primary" type="submit" disabled={!canSubmit} loading={create.isPending}>
            {t("prod.submit")}
          </Button>
          {create.isSuccess && (
            <span role="status" className="form-ok">
              {t("prod.success")}
            </span>
          )}
          {isConflict && (
            <span role="alert" className="form-error" data-testid="prod-conflict">
              {t("cat.carrier.conflict")}
            </span>
          )}
          {create.error instanceof ProblemError && !isConflict && (
            <span role="alert" className="form-error">
              {create.error.problem.detail}
            </span>
          )}
        </div>
      </form>
    </Card>
  );
}

function SalesStatusSelect({
  value,
  onChange,
}: {
  value: SalesStatus;
  onChange: (v: SalesStatus) => void;
}) {
  const { t } = useT();
  return (
    <Select
      label={t("prod.salesStatus")}
      value={value}
      onChange={(e) => onChange(e.target.value as SalesStatus)}
    >
      {SALES_STATUSES.map((s) => (
        <option key={s} value={s}>
          {t(`prod.sales.${s}`)}
        </option>
      ))}
    </Select>
  );
}
